// Отклики кандидата. Сценарий 87: повторный отклик того же человека НЕ заводит второй
// карточки — он прикрепляется к найденной с пометкой «повторный отклик». Вкладка существует
// ровно ради того, чтобы эту пометку было видно: без неё «человек откликнулся трижды» и
// «трое разных людей» на экране выглядят одинаково.
//
// Три состояния, которые здесь не слипаются (правило 7): откликов не было вовсе (карточку
// завели руками), отклики есть, и отклик пришёл, но телефон соискатель не открыл — на
// площадке это разные события, и второе не означает отказа.

import { RiInboxLine } from '@remixicon/react';

import { parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { EmptyState } from '@/shared/ui/screen-state';

import type { CandidateApplicationOut, CandidateOut } from '../api/candidates';

export function ApplicationsTab({ candidate }: { candidate: CandidateOut }) {
  const отклики = candidate.applications ?? [];

  if (отклики.length === 0) {
    return (
      <div className='flex h-full items-center justify-center p-5'>
        <EmptyState
          icon={RiInboxLine}
          title='Откликов с площадки не было'
          description='Карточка заведена руками либо человек пришёл другим путём. Отклики появляются здесь сами, как только площадка пришлёт уведомление либо рекрутёр заберёт их вручную из карточки учётной записи.'
        />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3 p-5'>
      <p className='text-paragraph-sm text-text-sub-600'>
        Откликов: {отклики.length}. Повторный отклик не заводит второй карточки — он прикрепляется
        сюда, и по этому списку видно, сколько раз человек к нам возвращался.
      </p>
      {отклики.map((отклик) => (
        <FieldList key={отклик.id} title={заголовок(отклик)}>
          <FieldRow
            label='Когда пришёл'
            value={
              отклик.received_at === null
                ? null
                : (parseMoment(отклик.received_at)?.toLocaleString('ru-RU') ?? null)
            }
            missing='площадка времени не прислала, взяли время приёма'
            note={parseMoment(отклик.created_at)?.toLocaleString('ru-RU') ?? undefined}
          />
          <FieldRow
            label='Что сделал соискатель'
            value={отклик.kind}
            missing='площадка не уточнила: написал он или посмотрел номер'
          />
          <FieldRow
            label='По какой вакансии'
            value={отклик.vacancy_external_id}
            missing='вакансия не названа'
          />
          <FieldRow
            label='Чат площадки'
            value={отклик.external_chat_id}
            missing='чата нет: соискатель не писал'
          />
        </FieldList>
      ))}
    </div>
  );
}

function заголовок(отклик: CandidateApplicationOut): string {
  return отклик.is_repeat ? 'Повторный отклик' : 'Первый отклик';
}

/** Метка повторного отклика в списке. Отдельно, чтобы её было видно и в шапке блока. */
export function RepeatBadge({ repeat }: { repeat: boolean }) {
  return repeat ? (
    <Badge.Root variant='lighter' color='orange' size='medium'>
      Повторный
    </Badge.Root>
  ) : null;
}
